import ProTable, { ActionType, ProColumns } from '@ant-design/pro-table';
import React, { useEffect, useRef, useState } from 'react';
import axiosApiInstance from '../../../../api/axiosClient';
import { Button, Form, Input, Spin } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { ModalForm, ProFormItem, ProFormText } from '@ant-design/pro-form';
import { useAtomValue, useSetAtom } from 'jotai';
import {assetLabelsAtom } from '../ddex_atoms';
import { queryClientAtom } from 'jotai-tanstack-query';
import { UserAuth } from '../../../../context/AuthContext';
import {useCan} from "../../../../context/auth_atoms";

const { Search } = Input;

const NewLabel = () => {
    const [form] = Form.useForm<API.DDEX.Label>();
    const queryClient = useAtomValue(queryClientAtom);
    return (
        <ModalForm<API.DDEX.Label>
            title='Add new Asset Label'
            width={1000}
            form={form}
            labelCol={{ span: 3 }}
            wrapperCol={{ span: 21 }}
            trigger={
                <Button type='primary' icon={<PlusOutlined />} style={{ width: '100%' }}>
                    Add
                </Button>
            }
            modalProps={{
                destroyOnClose: true,
                onCancel: () => true
            }}
            onFinish={async (values) => {
                await axiosApiInstance.post('/api/products/asset_labels/', values);
                // stepFormRef.current?.resetFields(['label_id']);
                queryClient.invalidateQueries({ queryKey: ['asset_labels'] });
                return true;
            }}
        >
            <ProFormText key={'full_name'} name={'name'} label='Name' />
            <ProFormText key={'p_line'} name={'p_line'} label='(P) Line' />
            <ProFormText key={'c_line'} name={'c_line'} label='(C) Line' />
        </ModalForm>
    );
};

interface DataType {
    key: React.Key;
    name: string;
    age: number;
    address: string;
}

const columns = [
    {
        title: 'Name',
        dataIndex: 'name',
        render: (text: string) => <a>{text}</a>
    },
    {
        title: 'Age',
        dataIndex: 'age'
    },
    {
        title: 'Address',
        dataIndex: 'address'
    }
];

const data = [
    {
        key: '1',
        name: 'John Brown',
        age: 32,
        address: 'New York No. 1 Lake Park'
    },
    {
        key: '2',
        name: 'Jim Green',
        age: 42,
        address: 'London No. 1 Lake Park'
    },
    {
        key: '3',
        name: 'Joe Black',
        age: 32,
        address: 'Sydney No. 1 Lake Park'
    },
    {
        key: '4',
        name: 'Disabled User',
        age: 99,
        address: 'Sydney No. 1 Lake Park'
    }
];

const TableFormTest = () => {
    const tableRef = useRef<any>();
    return (
        <ModalForm onFinish={async (values) => console.log(values)} trigger={<Button>Test</Button>}>
            <ProFormItem>
                <ProTable columns={columns} dataSource={data} />
            </ProFormItem>
        </ModalForm>
    );
};

const AssetLabelsTable = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useAtomValue(queryClientAtom);
    const { data: assetLabels, status: statusAssetLabels } = useAtomValue(assetLabelsAtom);
    
    const can = useCan();
    const canEdit = can('write', 'music::data::assetlabels');

    const [searchText, setSearchText] = useState('');
    const [filteredData, setFilteredData] = useState<API.MusicDistribution.AssetLabel[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (assetLabels) {
            const filtered = assetLabels.filter((label) =>
                label.ID.toString().toLowerCase().includes(searchText.toLowerCase()) ||
                label.name.toLowerCase().includes(searchText.toLowerCase()) ||
                (label.p_line && label.p_line.toLowerCase().includes(searchText.toLowerCase())) ||
                (label.c_line && label.c_line.toLowerCase().includes(searchText.toLowerCase()))
            );
            setFilteredData(filtered);
            setLoading(false);
        }
    }, [searchText, assetLabels]);

    const columns: ProColumns<API.MusicDistribution.AssetLabel>[] = [
        {
            title: 'id',
            key: 'id',
            dataIndex: 'ID',
            hideInSearch: true,
            hideInForm: true
        },
        {
            title: 'Name',
            key: 'name',
            dataIndex: 'name',
            hideInSearch: true,
            hideInForm: true
        },
        {
            title: '(P) Line',
            key: 'p_line',
            dataIndex: 'p_line',
            hideInSearch: true,
            hideInForm: true
        },
        {
            title: '(C) Line',
            key: 'c_line',
            dataIndex: 'c_line',
            hideInSearch: true,
            hideInForm: true
        },
        {
            title: '',
            key: 'actions',
            search: false,
            render(_dom, entity) {
                return (
                    <>
                        <Button
                            type='link'
                            icon={<EditOutlined />}
                            onClick={() =>
                                navigate(`/music/asset_labels/${entity.ID}/edit`, {
                                    state: { background: location }
                                })
                            }
                        />
                        <Button
                            type='link'
                            icon={<DeleteOutlined />}
                            danger
                            onClick={() => {
                                navigate(`/music/asset_labels/${entity.ID}/delete`, {
                                    state: { background: location }
                                });
                            }}
                        />
                    </>
                );
            }
        }
    ];
    return (
        <>
        <Search
        placeholder="Search asset labels..."
        allowClear
        onChange={(e) => setSearchText(e.target.value)}
        style={{ marginBottom: 16 }}
    />
        <Spin spinning={loading}> 
        <ProTable
            columns={columns}
            search={false}
            dataSource={filteredData}
            rowKey='ID'
            options={{
                reload: async () => {
                    await queryClient.invalidateQueries({ queryKey: ['asset_labels'] });
                }
            }}
            toolBarRender={() => (canEdit ? [<NewLabel />] : [])}
        />
        </Spin>
        </>
    );
};

export default AssetLabelsTable;